import axios from 'axios';
import { getApiBase } from './apiBase';
import keycloak from './keycloak';

const apiClient = axios.create({
  baseURL: getApiBase(),
  timeout: 60000,
});

// Always attach the latest Keycloak token from localStorage.
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('infinity_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let subscribers = [];

function onRefreshed(newToken) {
  subscribers.forEach((cb) => cb(newToken));
  subscribers = [];
}

function restartLogin() {
  localStorage.removeItem('infinity_token');
  delete apiClient.defaults.headers.common['Authorization'];
  // An expired access token is not an explicit sign-out. Restart the OIDC flow
  // so Keycloak can silently reuse a valid Remember Me session instead of
  // deleting it with a full logout.
  try {
    Promise.resolve(keycloak.login({ redirectUri: window.location.href }))
      .catch(() => { window.location.href = '/'; });
  } catch {
    window.location.href = '/';
  }
}

apiClient.interceptors.response.use(
  (r) => r,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Use Keycloak's built-in token refresh (PKCE / refresh_token grant).
          // Race against a timeout: if the IdP/refresh hangs, fail fast and force
          // re-login instead of leaving the request (and the UI action) stuck.
          await Promise.race([
            keycloak.updateToken(5),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Token refresh timed out')), 8000)
            ),
          ]);
          const newToken = keycloak.token;
          localStorage.setItem('infinity_token', newToken);
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          isRefreshing = false;
          onRefreshed(newToken);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch {
          isRefreshing = false;
          subscribers = [];
          restartLogin();
          return Promise.reject(error);
        }
      }

      return new Promise((resolve) => {
        subscribers.push((newToken) => {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          resolve(apiClient(originalRequest));
        });
      });
    }
    return Promise.reject(error);
  }
);

export { apiClient };
